import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ShoppingBag, User } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";

export const scrollTo = (target) => {
  if (window.__lenis) window.__lenis.scrollTo(target, { offset: -72, duration: 1.6 });
  else document.querySelector(target)?.scrollIntoView({ behavior: "smooth" });
};

const Nav = () => {
  const { count, setOpen } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 1, delay: 7.8, ease: [0.16, 1, 0.3, 1] }}
      className="fixed top-0 left-0 right-0 z-40 backdrop-blur-xl bg-[#F9F8F6]/70 border-b border-[#EBE8E3]"
      data-testid="main-nav"
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 h-[72px] flex items-center justify-between">
        <button
          onClick={() => scrollTo("#top")}
          className="flex items-center gap-4 group"
          data-testid="nav-logo"
        >
          <img src="/assets/dna-mark.png?v=6" alt="DNA" className="h-7 md:h-8 w-auto" />
          <span className="hidden md:block text-sm font-medium tracking-[0.3em] uppercase text-[#111111] group-hover:text-[#C07C5F] transition-colors duration-500">
            Daily Nutrition Aesthetics
          </span>
        </button>

        <nav className="hidden md:flex items-center gap-8 text-[11px] tracking-[0.3em] uppercase text-[#5C5C5C]">
          {[
            ["Collection", "#collection", "nav-link-collection"],
            ["Formula", "#formula", "nav-link-formula"],
            ["Calculator", "#calculator", "nav-link-calculator"],
            ["Your Box", "#build-box", "nav-link-build-box"],
            ["Manifesto", "#manifesto", "nav-link-manifesto"],
            ["Family", "#reviews", "nav-link-reviews"],
            ["FAQ", "#faq", "nav-link-faq"],
          ].map(([label, href, tid]) => (
            <button
              key={href}
              onClick={() => scrollTo(href)}
              data-testid={tid}
              className="relative hover:text-[#111111] transition-colors duration-500 after:absolute after:left-0 after:-bottom-1 after:h-px after:w-0 after:bg-[#C07C5F] after:transition-[width] after:duration-500 hover:after:w-full"
            >
              {label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(user ? "/account" : "/login")}
            data-testid="nav-account-link"
            className="flex items-center gap-2 border border-[#111111] rounded-full px-4 py-2 text-[11px] tracking-[0.25em] uppercase hover:bg-[#111111] hover:text-[#F9F8F6] transition-colors duration-500"
          >
            <User size={14} strokeWidth={1.5} />
            <span className="hidden md:inline">{user ? user.name.split(" ")[0] : "Login"}</span>
          </button>
          <button
            onClick={() => setOpen(true)}
            data-testid="cart-drawer-trigger"
            className="relative flex items-center gap-2 border border-[#111111] rounded-full px-5 py-2 text-[11px] tracking-[0.25em] uppercase hover:bg-[#111111] hover:text-[#F9F8F6] transition-colors duration-500"
          >
            <ShoppingBag size={14} strokeWidth={1.5} />
            <span>Cart</span>
            {count > 0 && (
              <span
                data-testid="cart-count-badge"
                className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-[#C07C5F] text-white text-[10px] flex items-center justify-center"
              >
                {count}
              </span>
            )}
          </button>
        </div>
      </div>
    </motion.header>
  );
};

export default Nav;
